/**
 * usageMetrics — THE client entry to the self-hosted usage/perf event pipeline
 * (P2 observability, 2026-08). Counterpart of POST /api/usage-events.
 *
 * Contract: absolutely fire-and-forget. A metrics call must never throw, never
 * block, never surface an error to the user, and never matter to the feature
 * that called it. If analytics is down, the product doesn't notice.
 *
 * Event names are dot-namespaced lowercase (validated server-side):
 *   app.login, site.open, viewer.3d.open, viewer.panorama.open,
 *   poi.focus, poi.search, file.upload, perf.web-vitals, perf.model-load
 */

import { apiService } from './ApiService';

const firedThisSession = new Set<string>();

export interface TrackOptions {
  /** Owning site id, when the event is site-scoped. */
  assetId?: string;
  /** Small JSON payload (server caps at 4KB). */
  props?: Record<string, unknown>;
  /** Deduplicate per browser session (keyed by event+assetId). */
  oncePerSession?: boolean;
}

// Tiny span util for client perf marks: markSpanStart at the trigger, and the
// completion callback reports elapsed ms. Single-shot per name (re-marking
// overwrites; ending twice reports once).
const spanStarts = new Map<string, number>();

export const markSpanStart = (name: string): void => {
  try { spanStarts.set(name, performance.now()); } catch { /* fire-and-forget */ }
};

export const trackSpanEnd = (event: string, name: string, opts?: TrackOptions): void => {
  try {
    const start = spanStarts.get(name);
    if (start === undefined) return;
    spanStarts.delete(name);
    const ms = Math.round(performance.now() - start);
    trackEvent(event, { ...opts, props: { ...opts?.props, ms } });
  } catch { /* fire-and-forget */ }
};

export const trackEvent = (event: string, opts?: TrackOptions): void => {
  try {
    if (opts?.oncePerSession) {
      const key = `${event}|${opts?.assetId ?? ''}`;
      if (firedThisSession.has(key)) return;
      firedThisSession.add(key);
    }
    // Park on the auth-ready barrier: metrics can fire pre-auth (web-vitals,
    // boot paths), and an unauthenticated POST would 401 into the global
    // logout callback. Values are captured now, sent when auth lands.
    void apiService
      .whenAuthReady()
      .then(() => apiService.post('/usage-events', { event, assetId: opts?.assetId, props: opts?.props }))
      .catch(() => { /* fire-and-forget */ });
  } catch { /* fire-and-forget */ }
};
